import '../style/global.css';

import { InfoOutlined } from '@mui/icons-material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormLabel, IconButton, Paper, Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ScoreGrade, scoreColor } from '../Score';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { AudioApi } from '../api/AudioApi';
import { ISentenceScore } from '../api/ScoreApi';
import { AlignmentErrorEnum, ISentenceScoreManualGrade, ISentenceScoreManualIndividualGrade, ManualGradeEnum, SentenceScoreManualGradeApi } from '../api/SentenceScoreManualGradeApi';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useKaiTiFontStyles } from '../font/KaiTiFont';

export default function ManualGradeAudio() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.ManualGradeAudio)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio, isPlaying } = useAudioPlayer();

    const [sentenceScore, setSentenceScore] = React.useState<ISentenceScore | null>(null);
    const [sentenceScoreAudio, setSentenceScoreAudio] = React.useState<Blob | null>(null);
    const [playingAudioSentenceScoreId, setPlayingAudioSentenceScoreId] = React.useState<number | null>(null);
    const kaiTiFontClass = useKaiTiFontStyles();

    const [isReadyToGrade, setIsReadyToGrade] = React.useState(false);
    const [isSaved, setIsSaved] = React.useState(false)

    const [isLastFetchByRandom, setLastFetchByRandom] = React.useState(false);

    const emptyManualForm = {
        id: 0,
        sentence_score_id: 0,      // set once we have a sentenceScore
        user_id: 0,         // set from your auth context if you have it
        manual_grade: null,
        has_extra_or_missing: null,
        incorrect_word_count: null,
        sentence_score_manual_individual_grade_list: null,
        created_at: null,
        updated_at: null
    }

    const [manualGradeForm, setManualGradeForm] = React.useState<ISentenceScoreManualGrade>(emptyManualForm);

    // Add near other useState hooks
    const [manualIndividualGradeList, setManualIndividualGradeList] = React.useState<ISentenceScoreManualIndividualGrade[]>([]);


    const [page, setPage] = React.useState(0);               // MUI is 0-based
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [totalRows, setTotalRows] = React.useState(0);
    const [pagedRows, setPagedRows] = React.useState<ISentenceScoreManualGrade[]>([]);
    const [listLoading, setListLoading] = React.useState(false);

    type SortBy = 'id' | 'date';
    const [sortBy, setSortBy] = React.useState<SortBy>('id');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const fetchSentenceScoreManualGradePage = React.useCallback((nextPage: number = page, nextRowsPerPage: number = rowsPerPage, nextSortBy: SortBy = sortBy, nextSortOrder: 'asc' | 'desc' = sortOrder) => {
        setListLoading(true);
        // server expects 0-based page
        SentenceScoreManualGradeApi.getPage(nextPage, nextRowsPerPage, nextSortBy, nextSortOrder)
            .then(resp => {
                if (resp.status >= 300) throw new Error(`List failed ${resp.status}`);
                const data = resp.data;
                setPagedRows(data.items || []);
                setTotalRows(data.total || 0);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => setListLoading(false));
    }, [page, rowsPerPage, sortBy, sortOrder, setErrorMessage, history, location]);

    React.useEffect(() => {
        fetchSentenceScoreManualGradePage(page, rowsPerPage, sortBy, sortOrder);
    }, [page, rowsPerPage, sortBy, sortOrder, fetchSentenceScoreManualGradePage]);

    const fetchRandomSentenceScore = () => {
        SentenceScoreManualGradeApi.getRandom()
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting random audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(manualGrade => {
                if (manualGrade.sentence_score) {
                    setSentenceScore(manualGrade.sentence_score)
                }
                setManualGradeForm(manualGrade)
                setIsReadyToGrade(false)
                setIsSaved(false)
                if (manualGrade.sentence_score_manual_individual_grade_list) {
                    setManualIndividualGradeList(manualGrade.sentence_score_manual_individual_grade_list)
                }
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }

    const getAudio = React.useCallback((sentenceScoreId: number) => {
        AudioApi.getSentenceScoreAudio(sentenceScoreId)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to ScoreApi.getScoreBySentence() " + response.status)
            })
            .then(audioData => {
                setSentenceScoreAudio(audioData)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [setSentenceScoreAudio, setErrorMessage, history, location]);

    React.useEffect(() => {
        if (sentenceScore) {
            getAudio(sentenceScore.id)
        }
    }, [sentenceScore, getAudio])

    React.useEffect(() => {
        if (sentenceScoreAudio) {
            playAudio(sentenceScoreAudio);
        }
    }, [sentenceScoreAudio, playAudio])


    const addOrUpdate = (manualGrade: ISentenceScoreManualGrade) => {
        if (!manualGrade.id || manualGrade.id <= 0) {
            SentenceScoreManualGradeApi.create(manualGrade)
                .then(response => {
                    if (response.status < 300) {
                        return response.data;
                    }
                    throw new Error("Unable to SentenceScoreManualGradeApi.update " + response.status)
                })
                .then(() => {
                    setIsSaved(true)
                    // fetchSentenceScoreManualGradeList()
                    fetchSentenceScoreManualGradePage(page, rowsPerPage);  // go back to first page or keep `page`
                    toast.success("Saved, fetch next audio", { autoClose: 3000, hideProgressBar: true })
                    if (isLastFetchByRandom) {
                        fetchRandomSentenceScore()
                    } else {
                        setManualGradeForm(emptyManualForm)
                        setSentenceScore(null)
                    }
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        } else {
            SentenceScoreManualGradeApi.update(manualGrade)
                .then(response => {
                    if (response.status < 300) {
                        return response.data;
                    }
                    throw new Error("Unable to SentenceScoreManualGradeApi.update " + response.status)
                })
                .then(() => {
                    setIsSaved(true)
                    // fetchSentenceScoreManualGradeList()
                    fetchSentenceScoreManualGradePage(page, rowsPerPage);  // go back to first page or keep `page`
                    toast.success("Saved, fetch next audio", { autoClose: 3000, hideProgressBar: true })
                    if (isLastFetchByRandom) {
                        fetchRandomSentenceScore()
                    } else {
                        setManualGradeForm(emptyManualForm)
                        setSentenceScore(null)
                    }
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }

    }

    const handleReadyToGrade = () => {
        setIsReadyToGrade(true)
        if (sentenceScoreAudio) {
            setTimeout(() => {
                playAudio(sentenceScoreAudio)
            }, 500); // 500 ms = 0.5s
        }
    }

    const handleSubmitManualGrade = async () => {
        try {
            if (sentenceScore) {
                const payload: ISentenceScoreManualGrade = {
                    id: manualGradeForm.id,
                    sentence_score_id: sentenceScore.id,
                    user_id: 0,
                    manual_grade: manualGradeForm.manual_grade,
                    has_extra_or_missing: manualGradeForm.has_extra_or_missing,
                    incorrect_word_count: manualGradeForm.incorrect_word_count,
                    sentence_score_manual_individual_grade_list: null,
                    created_at: null,
                    updated_at: null
                };
                addOrUpdate(payload);
            } else {
                throw Error("Sentence Score is null")
            }
        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    };

    // const updateIndividualAt = (index: number, patch: Partial<ISentenceScoreManualIndividualGrade>) => {
    //     setManualIndividualGradeList(prev => {
    //         const next = prev.slice();
    //         const ix = next.findIndex(x => x.index === index);
    //         if (ix >= 0) next[ix] = { ...next[ix], ...patch };
    //         setIsSaved(false);
    //         return next;
    //     });
    // };
    const updateIndividualAt = (index: number, patch: Partial<ISentenceScoreManualIndividualGrade>) => {
        setManualIndividualGradeList(manualGradeList => {
            const manualGradeListCopy = manualGradeList.slice();
            const ix = manualGradeListCopy.findIndex(x => x.index === index);
            if (ix < 0) return manualGradeList;

            const currentManualGrade = manualGradeListCopy[ix];
            let updatedManualGrade: ISentenceScoreManualIndividualGrade = { ...currentManualGrade, ...patch };

            const alignmentError = patch.alignment_error;

            if (alignmentError === AlignmentErrorEnum.Insertion) {
                // Rule 1: Insertion → all Bad, because we only insert if no transcript
                updatedManualGrade = {
                    ...updatedManualGrade,
                    initial_score_grade: ScoreGrade.Bad,
                    final_score_grade: ScoreGrade.Bad,
                    tone_score_grade: ScoreGrade.Bad,
                    score_grade: ScoreGrade.Bad,
                };
            } else if (alignmentError === AlignmentErrorEnum.Deletion) {
                // Rule 2: Deletion + transcript_char != '-' → all Bad
                if (updatedManualGrade.transcript_char !== '-') {
                    updatedManualGrade = {
                        ...updatedManualGrade,
                        initial_score_grade: ScoreGrade.Bad,
                        final_score_grade: ScoreGrade.Bad,
                        tone_score_grade: ScoreGrade.Bad,
                        score_grade: ScoreGrade.Bad,
                    };
                } else {
                    updatedManualGrade = {
                        ...updatedManualGrade,
                        initial_score_grade: null,
                        final_score_grade: null,
                        tone_score_grade: null,
                        score_grade: null,
                    };
                }
                // Rule 2: No Alignment Error? probaby coming from DELETION, set score-grade bad if they are null
            } else if (alignmentError === AlignmentErrorEnum.None) {
                const initialScoreGrade = updatedManualGrade.initial_score_grade == null ? ScoreGrade.Bad : updatedManualGrade.initial_score_grade
                const finalScoreGrade = updatedManualGrade.final_score_grade == null ? ScoreGrade.Bad : updatedManualGrade.final_score_grade
                const toneScoreGrade = updatedManualGrade.tone_score_grade == null ? ScoreGrade.Bad : updatedManualGrade.tone_score_grade
                const scoreGrade = combineScoreGrade(initialScoreGrade, finalScoreGrade, toneScoreGrade)

                updatedManualGrade = {
                    ...updatedManualGrade,
                    initial_score_grade: initialScoreGrade,
                    final_score_grade: finalScoreGrade,
                    tone_score_grade: toneScoreGrade,
                    score_grade: scoreGrade,
                };

            } else {
                // No insertion/deletion forcing — if any sub-grade changed, keep score_grade in sync
                const touchedSubgrade =
                    'initial_score_grade' in patch ||
                    'final_score_grade' in patch ||
                    'tone_score_grade' in patch;

                if (touchedSubgrade) {
                    const updatedAlignmentError = updatedManualGrade.transcript_char === '-' ? AlignmentErrorEnum.Insertion : AlignmentErrorEnum.Deletion
                    const updatedScoreGrade = combineScoreGrade(updatedManualGrade.initial_score_grade, updatedManualGrade.final_score_grade, updatedManualGrade.tone_score_grade)
                    updatedManualGrade = {
                        ...updatedManualGrade,
                        alignment_error: updatedAlignmentError,
                        score_grade: updatedScoreGrade
                    };
                }
            }

            manualGradeListCopy[ix] = updatedManualGrade;
            setIsSaved(false);
            return manualGradeListCopy;
        });
    };

    const handleSubmitIndividualGrade = async () => {
        try {
            if (sentenceScore) {
                const individualGradeList: ISentenceScoreManualIndividualGrade[] = manualIndividualGradeList.map(x => {
                    // make a shallow copy so we can mutate
                    const item = {
                        ...x,
                        id: x.id || 0,
                        sentence_score_manual_grade_id: manualGradeForm.id || 0
                    };

                    if (item.alignment_error == null) {
                        item.alignment_error = AlignmentErrorEnum.None
                    }
                    return item;
                });

                const payload: ISentenceScoreManualGrade = {
                    id: manualGradeForm.id,
                    sentence_score_id: sentenceScore.id,
                    user_id: 0,
                    manual_grade: null,
                    has_extra_or_missing: null,
                    incorrect_word_count: null,
                    sentence_score_manual_individual_grade_list: individualGradeList,
                    created_at: null,
                    updated_at: null
                };
                addOrUpdate(payload);
            } else {
                throw Error("Sentence Score is null")
            }
        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    };

    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState<ISentenceScoreManualGrade | null>(null);

    const openDelete = (row: ISentenceScoreManualGrade) => {
        setDeleteTarget(row);
        setDeleteOpen(true);
    };
    const closeDelete = () => {
        setDeleteOpen(false);
        setDeleteTarget(null);
    };

    // const sortedManualGradeList = React.useMemo(() => {
    //     return [...sentenceScoreManualGradeList].sort((a, b) => b.id - a.id);
    // }, [sentenceScoreManualGradeList]);

    const playSentenceScoreAudio = (sentenceScoreId: number) => {
        setPlayingAudioSentenceScoreId(sentenceScoreId)
        AudioApi.getSentenceScoreAudio(sentenceScoreId)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to ScoreApi.getScoreBySentence() " + response.status)
            })
            .then(audioData => {
                playAudio(audioData, {
                    onEnded: () => {
                        setPlayingAudioSentenceScoreId(null)
                    },
                    onError: () => {
                        setPlayingAudioSentenceScoreId(null)
                    }
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const TinyGradeChip: React.FC<{
        scoreGrade: ScoreGrade | null;
        onChange?: (scoreGrade: ScoreGrade) => void;
        title?: string;
        disabled?: boolean;
    }> = ({ scoreGrade: value, onChange, title, disabled = false }) => {
        const clickTimerRef = React.useRef<number | null>(null);
        const longTimerRef = React.useRef<number | null>(null);
        const longFiredRef = React.useRef<boolean>(false);

        const clearClick = () => {
            if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
            }
        };
        const clearLong = () => {
            if (longTimerRef.current) {
                clearTimeout(longTimerRef.current);
                longTimerRef.current = null;
            }
        };

        const handlePointerDown = () => {
            longFiredRef.current = false;
            clearLong();
            // long press → Bad
            longTimerRef.current = window.setTimeout(() => {
                longFiredRef.current = true;
                clearClick();
                if (onChange) onChange(ScoreGrade.Bad);
            }, 600);
        };

        const handlePointerUpOrLeave = () => {
            clearLong();
        };

        const handleClick = () => {
            // ignore click if long already fired
            if (longFiredRef.current) return;
            clearClick();
            // single click → Good (delay so double-click can cancel)
            clickTimerRef.current = window.setTimeout(() => {
                if (onChange) onChange(ScoreGrade.Good);
                clearClick();
            }, 220);
        };

        const handleDoubleClick = () => {
            // double click → OK
            clearClick();
            if (onChange) onChange(ScoreGrade.OK);
        };

        return (
            <Box
                role="button"
                // tabIndex={disabled ? -1 : 0}  // allow clicking, parent should set alignmentError=None
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUpOrLeave}
                onPointerLeave={handlePointerUpOrLeave}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                onKeyDown={(e) => {
                    if (!onChange) return;
                    // if (disabled) return;  // allow clicking, parent should set alignmentError=None
                    // keyboard support: Enter = Good, Shift+Enter = OK, Space (long) → Bad is tricky;
                    // map Space to OK for convenience
                    if (e.key === 'Enter' && !e.shiftKey) onChange(ScoreGrade.Good);
                    else if ((e.key === 'Enter' && e.shiftKey) || e.key === ' ') onChange(ScoreGrade.OK);
                }}
                // aria-disabled={disabled}  // allow clicking, parent should set alignmentError=None
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 2,
                    // bgcolor: `${scoreColor(value)}22`,
                    bgcolor: disabled || value == null ? "#dddddd" : `${scoreColor(value)}22`, // 👈 grey when disabled
                    // cursor: disabled ? 'not-allowed' : 'pointer',  // allow clicking, parent should set alignmentError=None
                    outline: 'none',
                    // opacity: disabled ? 0.4 : 1,
                    // pointerEvents: disabled ? 'none' : 'auto', // block mouse/touch  // allow clicking, parent should set alignmentError=None
                }}
                title={`${title ? title + ": " : ""}${value}`}
                aria-label={`${title ? title + " grade: " : ""}${value}. Click=Good, Double-click=OK, Long-press=Bad.`}
            />
        );
    };

    const renderIndividualGrades = (manualGrade: ISentenceScoreManualGrade) => {
        // show small colored chips for the sequence
        let list: ISentenceScoreManualIndividualGrade[] = [];
        if (manualGrade.sentence_score_manual_individual_grade_list) {
            list = manualGrade.sentence_score_manual_individual_grade_list
        }
        let text = ""
        if (manualGrade.sentence_score && manualGrade.sentence_score.text) {
            text = manualGrade.sentence_score.text
        }

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="flex-start">
                {list.map((it, i) => {
                    const alignmentError = it.alignment_error;

                    // choose label if error
                    let errorLabel: string | null = null;
                    if (alignmentError && alignmentError !== AlignmentErrorEnum.None) {
                        if (alignmentError === AlignmentErrorEnum.Deletion) errorLabel = "sil";
                        else if (alignmentError === AlignmentErrorEnum.Insertion) errorLabel = "ins";
                    }

                    return <Stack key={i} spacing={0.5} alignItems="center">
                        <Box
                            sx={{
                                width: 24, height: 24, borderRadius: 2,
                                bgcolor: `${it.score_grade ? scoreColor(it.score_grade) : "#000000"}22 `,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                px: 0.5
                            }}
                            title={it.score_grade ? it.score_grade : "ERR"}
                        >
                            {text[i]}
                        </Box>
                        {errorLabel ? (
                            <Box
                                sx={{
                                    width: 24,
                                    height: 80,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                                >
                                    {errorLabel}
                                </Typography>

                            </Box>
                        ) : (
                            <Stack direction="column" spacing={0.5} sx={{ transformOrigin: 'top center' }}>
                                <TinyGradeChip scoreGrade={it.initial_score_grade} />
                                <TinyGradeChip scoreGrade={it.final_score_grade} />
                                <TinyGradeChip scoreGrade={it.tone_score_grade} />
                            </Stack>
                        )}
                    </Stack>
                })}
            </Stack>
        );
    };


    // const markAllAsAlignmentErrorDeletion = () => {
    //     setManualIndividualGradeList((prev) =>
    //         prev.map((x) => ({ ...x, alignment_error: AlignmentErrorEnum.Deletion }))
    //     );
    // };
    const markAllAsAlignmentErrorDeletion = () => {
        setManualIndividualGradeList((prev) =>
            prev.map((individalGrade) => {
                return {
                    ...individalGrade,
                    alignment_error: AlignmentErrorEnum.Deletion,
                    initial_score_grade: individalGrade.transcript_char == '-' ? null : ScoreGrade.Bad,
                    final_score_grade: individalGrade.transcript_char == '-' ? null : ScoreGrade.Bad,
                    tone_score_grade: individalGrade.transcript_char == '-' ? null : ScoreGrade.Bad,
                    score_grade: individalGrade.transcript_char == '-' ? null : ScoreGrade.Bad
                };
            })
        );
    };

    const renderSummaryTriple = (row: ISentenceScoreManualGrade) => (
        <Stack spacing={0.5}>
            <Typography variant="body2"><b>Manual:</b> {row.manual_grade ? row.manual_grade : '—'}</Typography>
            <Typography variant="body2"><b>Extra/Missing:</b> {row.has_extra_or_missing == null ? '—' : (row.has_extra_or_missing ? 'Yes' : 'No')}</Typography>
            <Typography variant="body2"><b>Incorrect words:</b> {row.incorrect_word_count ? row.incorrect_word_count : '—'}</Typography>
        </Stack>
    );

    const handleEditRow = (manualGradeId: number) => {

        SentenceScoreManualGradeApi.getById(manualGradeId)
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting random audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(manualGrade => {
                if (manualGrade.sentence_score) {
                    setSentenceScore(manualGrade.sentence_score)
                }
                setManualGradeForm(manualGrade)
                // jump UI into “ready to grade” mode so user sees forms
                setIsReadyToGrade(true)
                setIsSaved(false)
                if (manualGrade.sentence_score) {
                    // pinyin_list_transcript is used to loop in the HTML below
                    const gradeList: ISentenceScoreManualIndividualGrade[] =
                        manualGrade.sentence_score.pinyin_list_transcript.map((pinyin_transcript, index) => ({
                            id: 0,  // new rows don’t exist in DB yet
                            sentence_score_manual_grade_id: manualGrade.id,
                            index,
                            score_grade: null,
                            initial_score_grade: null,
                            final_score_grade: null,
                            tone_score_grade: null,
                            alignment_error: null,
                            transcript_char: manualGrade.sentence_score ? manualGrade.sentence_score.text[index] : "ERR",
                            pinyin_spoken: manualGrade.sentence_score ? manualGrade.sentence_score.pinyin_list_spoken[index] : "ERR",
                            pinyin_transcript: manualGrade.sentence_score ? manualGrade.sentence_score.pinyin_list_transcript[index] : "ERR"
                        }));
                    if (manualGrade.sentence_score_manual_individual_grade_list) {
                        for (let i = 0; i < gradeList.length; i++) {
                            if (i < manualGrade.sentence_score_manual_individual_grade_list.length) {
                                const individiualGrade = manualGrade.sentence_score_manual_individual_grade_list[i]
                                gradeList[i].alignment_error = individiualGrade.alignment_error
                                if (individiualGrade.alignment_error == AlignmentErrorEnum.None || individiualGrade.alignment_error == null) {
                                    gradeList[i].initial_score_grade = individiualGrade.initial_score_grade
                                    gradeList[i].final_score_grade = individiualGrade.final_score_grade
                                    gradeList[i].tone_score_grade = individiualGrade.tone_score_grade
                                    gradeList[i].score_grade = combineScoreGrade(gradeList[i].initial_score_grade, gradeList[i].final_score_grade, gradeList[i].tone_score_grade)
                                } else if (individiualGrade.alignment_error == AlignmentErrorEnum.Insertion) {
                                    gradeList[i].initial_score_grade = ScoreGrade.Bad
                                    gradeList[i].final_score_grade = ScoreGrade.Bad
                                    gradeList[i].tone_score_grade = ScoreGrade.Bad
                                    gradeList[i].score_grade = ScoreGrade.Bad
                                } else if (individiualGrade.alignment_error == AlignmentErrorEnum.Deletion) {
                                    if (manualGrade.sentence_score.text[i] == '-') {
                                        gradeList[i].initial_score_grade = null
                                        gradeList[i].final_score_grade = null
                                        gradeList[i].tone_score_grade = null
                                        gradeList[i].score_grade = null
                                    } else {
                                        gradeList[i].initial_score_grade = ScoreGrade.Bad
                                        gradeList[i].final_score_grade = ScoreGrade.Bad
                                        gradeList[i].tone_score_grade = ScoreGrade.Bad
                                        gradeList[i].score_grade = ScoreGrade.Bad
                                    }
                                }
                            }
                        }
                    }
                    setManualIndividualGradeList(gradeList);
                } else {
                    setManualIndividualGradeList([]);
                }
                window.scrollTo({ top: 0, behavior: "smooth" })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const manualGradeId = deleteTarget.id;
            const sentenceScoreId = deleteTarget.sentence_score_id
            const resp = await SentenceScoreManualGradeApi.delete(manualGradeId);
            if (resp.status >= 300) throw new Error("Delete failed " + resp.status);
            closeDelete();
            // fetchSentenceScoreManualGradeList();
            fetchSentenceScoreManualGradePage(page, rowsPerPage);  // go back to first page or keep `page`
            if (sentenceScore && sentenceScoreId == sentenceScore.id) {
                setManualGradeForm(emptyManualForm)
                setSentenceScore(null)
            }
            toast.success("Deleted.", { autoClose: 3000, hideProgressBar: true });
        } catch (err: any) {
            closeDelete();
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    };

    const combineScoreGrade = (initialScoreGrade: ScoreGrade | null, finalScoreGrade: ScoreGrade | null, toneScoreGrade: ScoreGrade | null) => {
        // I think this "business logic" of combined score grade should live in backend somewhere, but not sure where to save it or send it.
        if (initialScoreGrade == null || finalScoreGrade == null || toneScoreGrade == null) {
            return null
        }

        let scoreGrade = ScoreGrade.Good
        if (initialScoreGrade == ScoreGrade.Bad || finalScoreGrade == ScoreGrade.Bad || toneScoreGrade == ScoreGrade.Bad) {
            scoreGrade = ScoreGrade.Bad
        } else if (initialScoreGrade == ScoreGrade.OK || finalScoreGrade == ScoreGrade.OK || toneScoreGrade == ScoreGrade.OK) {
            scoreGrade = ScoreGrade.OK
        }
        return scoreGrade;
    }

    const formatDateTime = (iso?: Date | null) => iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '—';

    return (
        <Stack direction="column" spacing={2} sx={{ mt: 2 }}>

            <Button variant="contained" color="primary" onClick={() => { fetchRandomSentenceScore(); setLastFetchByRandom(true); }} sx={{ alignSelf: 'center' }}>
                Fetch Random Audio
            </Button>

            {!isReadyToGrade && sentenceScore && sentenceScoreAudio && (
                <Stack direction="column" alignItems="center" sx={{ alignSelf: 'center' }} spacing={2}>
                    <IconButton onClick={() => playAudio(sentenceScoreAudio)}>
                        <img src={SpeakerBlueIcon} style={{ width: '36px', height: '36px' }} />
                    </IconButton>
                    <Typography>
                        Please listen to the audio before grading.<br />
                        Click the speaker to hear it again.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => handleReadyToGrade()}
                        disabled={isPlaying}
                    >
                        {isPlaying ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Ready to Grade!'
                        )}
                    </Button>
                </Stack>
            )}

            {isReadyToGrade && sentenceScore && (
                <Stack direction="column" alignItems="center" sx={{ alignSelf: 'center' }} spacing={2}>
                    <Stack direction="row" spacing={2} data-testid="grading-chip-row">

                        <Stack direction="column" alignItems="center" sx={{ alignSelf: 'flex-start' }} useFlexGap>
                            <Stack alignItems="center" spacing={2} sx={{ mt: 10 }}>
                                <Typography sx={{ height: "24px" }}>Initial</Typography>
                                <Typography sx={{ height: "24px" }}>Final</Typography>
                                <Typography sx={{ height: "24px" }}>Tone</Typography>
                                <Typography sx={{ height: "30px", pt: '4px' }}>Speech</Typography>
                                <Tooltip title="Student spoke an extra character" arrow>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography sx={{ height: "24px" }}>Extra Speech</Typography>
                                        <InfoOutlined sx={{ fontSize: 14, color: "#0062FF", cursor: "pointer" }} />
                                    </Stack>
                                </Tooltip>
                                <Tooltip title="Student didn't speak this character" arrow>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography sx={{ height: "24px" }}>Silence / Noise</Typography>
                                        <InfoOutlined sx={{ fontSize: 14, color: "#0062FF", cursor: "pointer" }} />
                                    </Stack>
                                </Tooltip>
                            </Stack>

                        </Stack>
                        {sentenceScore.pinyin_list_transcript.map((pinyinElement, index) => {
                            const manualIndividualGrade = manualIndividualGradeList.find(x => x.index === index);
                            if (manualIndividualGrade == null) return null;
                            // const chipDisabled = !!manualIndividualGrade.alignment_error && manualIndividualGrade.alignment_error !== AlignmentErrorEnum.None;
                            const transcriptBlank = sentenceScore.text[index] == '-'
                            const isGreyedOut = transcriptBlank && manualIndividualGrade.alignment_error == AlignmentErrorEnum.Deletion

                            const initialScoreGrade = manualIndividualGrade.initial_score_grade;
                            const finalScoreGrade = manualIndividualGrade.final_score_grade
                            const toneScoreGrade = manualIndividualGrade.tone_score_grade
                            const scoreGrade = combineScoreGrade(initialScoreGrade, finalScoreGrade, toneScoreGrade)


                            return (<>
                                <Stack key={index} alignItems="stretch" justifyContent="center">
                                    <Stack alignItems="center"
                                        sx={{
                                            backgroundColor: `${isGreyedOut ? "#dddddd" : `${scoreColor(ScoreGrade.Good)}22`}`,
                                            // backgroundColor: `${scoreColor(ScoreGrade.Good)}22`,
                                            borderTopLeftRadius: 4, borderTopRightRadius: 4, px: 1
                                        }}>
                                        <Typography>
                                            {pinyinElement || '-'}
                                        </Typography>
                                    </Stack>
                                    <Stack alignItems="center"
                                        sx={{
                                            backgroundColor: `${isGreyedOut || scoreGrade == null ? "#dddddd" : `${scoreColor(scoreGrade)}22`}`,
                                            // backgroundColor: `${scoreColor(combined_score_grade)}22`,
                                            borderBottomLeftRadius: 4, borderBottomRightRadius: 4, px: 1
                                        }}>
                                        <Typography className={kaiTiFontClass.customFont} sx={{ fontSize: '24px' }}>
                                            {sentenceScore.text[index]}
                                        </Typography>
                                    </Stack>
                                    <Stack alignItems="center" spacing={2} sx={{ pt: 2 }}>
                                        <Box sx={{ visibility: !transcriptBlank ? 'visible' : 'hidden', pointerEvents: !transcriptBlank ? 'auto' : 'none' }}>
                                            <TinyGradeChip
                                                scoreGrade={initialScoreGrade}
                                                onChange={(scoreGrade) => {
                                                    updateIndividualAt(index, { initial_score_grade: scoreGrade, alignment_error: AlignmentErrorEnum.None });
                                                }}
                                                disabled={isGreyedOut}
                                            />
                                        </Box>
                                        <Box sx={{ visibility: !transcriptBlank ? 'visible' : 'hidden', pointerEvents: !transcriptBlank ? 'auto' : 'none' }}>
                                            <TinyGradeChip
                                                scoreGrade={finalScoreGrade}
                                                onChange={(scoreGrade) => {
                                                    updateIndividualAt(index, { final_score_grade: scoreGrade, alignment_error: AlignmentErrorEnum.None });
                                                }}
                                                disabled={isGreyedOut}
                                            />
                                        </Box>
                                        <Box sx={{ visibility: !transcriptBlank ? 'visible' : 'hidden', pointerEvents: !transcriptBlank ? 'auto' : 'none' }}>
                                            <TinyGradeChip
                                                scoreGrade={toneScoreGrade}
                                                onChange={(scoreGrade) => {
                                                    updateIndividualAt(index, { tone_score_grade: scoreGrade, alignment_error: AlignmentErrorEnum.None });
                                                }}
                                                disabled={isGreyedOut}
                                            />
                                        </Box>
                                        <RadioGroup row={false} value={manualIndividualGrade.alignment_error || AlignmentErrorEnum.None}
                                            onChange={(e) => updateIndividualAt(index, { alignment_error: e.target.value as AlignmentErrorEnum })}
                                            sx={{
                                                alignItems: "center",
                                                "& .MuiRadio-root": {
                                                    height: 40,      // 👈 exact pixel height
                                                    p: 0,            // optional: remove extra padding
                                                },
                                            }}
                                        >
                                            <Radio size="small" value={AlignmentErrorEnum.None} sx={{ visibility: !transcriptBlank ? 'visible' : 'hidden', pointerEvents: !transcriptBlank ? 'auto' : 'none' }} />
                                            <Radio size="small" value={AlignmentErrorEnum.Insertion} sx={{ visibility: transcriptBlank ? 'visible' : 'hidden', pointerEvents: transcriptBlank ? 'auto' : 'none' }} />
                                            <Radio size="small" value={AlignmentErrorEnum.Deletion} />
                                        </RadioGroup>
                                    </Stack>
                                    <Stack alignItems="center"
                                        sx={(theme) => ({
                                            backgroundColor: theme.palette.action.disabledBackground, // faded background
                                            // backgroundColor: `${scoreColor(sentenceScore.score_grade[index])}22`,
                                            borderRadius: 4, px: 1
                                        })}>
                                        <Typography color="text.disabled">
                                            {sentenceScore.pinyin_list_spoken[index] || '-'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </>)
                        })}
                        <Stack
                            alignItems="center"
                            spacing={0.5}
                            sx={{ alignSelf: 'flex-end' }} // fill height + center vertically
                        >
                            {sentenceScoreAudio && (
                                <IconButton onClick={() => playAudio(sentenceScoreAudio)} sx={{ mt: 0.5, mb: 0 }} disabled={isPlaying}>
                                    {isPlaying ? (
                                        <CircularProgress size={24} color="inherit" style={{ width: '36px', height: '36px' }} />
                                    ) : (
                                        <img src={SpeakerBlueIcon} style={{ width: '36px', height: '36px' }} />
                                    )}
                                </IconButton>
                            )}
                            <Button
                                size="small"
                                variant="text"
                                onClick={markAllAsAlignmentErrorDeletion}
                            >
                                No words<br />were spoken
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleSubmitIndividualGrade}
                                disabled={isSaved}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                    <div>
                        <b>Single click</b>: Green / <b>Double Click</b>: Yellow / <b>Long Click</b>: Red
                    </div>
                </Stack>
            )}

            {isReadyToGrade && sentenceScore && (<>

                {/* --- Manual Grade Form --- */}
                <Divider sx={{ my: 1 }} />

                <FormControl
                    component="fieldset"
                    sx={{
                        alignSelf: "center",
                        width: "100%",
                        maxWidth: 720,
                        p: 2,
                    }}
                    disabled={!sentenceScore}
                >
                    <FormLabel component="legend" sx={{ mb: 2 }}>
                        Manual Grade
                    </FormLabel>

                    <Stack direction="row" spacing={4} alignItems="flex-start">
                        {/* Left column: Radio buttons */}
                        <Stack spacing={1}>
                            <RadioGroup
                                value={manualGradeForm.manual_grade ? manualGradeForm.manual_grade : ""}
                                onChange={(e) => {
                                    setManualGradeForm((prev) => ({
                                        ...prev,
                                        manual_grade: (e.target.value as ManualGradeEnum) || null,
                                    }))
                                    setIsSaved(false);
                                }}
                            >
                                <FormControlLabel
                                    value={ManualGradeEnum.NoProblem}
                                    control={<Radio />}
                                    label="No Problems (Green)"
                                />
                                <FormControlLabel
                                    value={ManualGradeEnum.MinorProblem}
                                    control={<Radio />}
                                    label="Minor Problems (Yellow)"
                                />
                                <FormControlLabel
                                    value={ManualGradeEnum.NeedFixes}
                                    control={<Radio />}
                                    label="Needs Fixes (Red)"
                                />
                                <FormControlLabel
                                    value={ManualGradeEnum.NothingSpoken}
                                    control={<Radio />}
                                    label="Nothing Spoken (Red)"
                                />
                            </RadioGroup>
                        </Stack>

                        {/* Right column: Other inputs and actions */}
                        <Stack spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!manualGradeForm.has_extra_or_missing}
                                        onChange={(e) => {
                                            setManualGradeForm((prev) => ({
                                                ...prev,
                                                has_extra_or_missing: e.target.checked,
                                            }))
                                            setIsSaved(false);
                                        }}
                                    />
                                }
                                label="Has extra or missing words"
                            />

                            <TextField
                                label="Incorrect word count"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={
                                    manualGradeForm.incorrect_word_count === null ||
                                        manualGradeForm.incorrect_word_count === undefined
                                        ? ""
                                        : manualGradeForm.incorrect_word_count
                                }
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                        setManualGradeForm((prev) => ({
                                            ...prev,
                                            incorrect_word_count: null,
                                        }));
                                        return;
                                    }
                                    const n = Math.max(0, parseInt(val, 10) || 0);
                                    setManualGradeForm((prev) => ({
                                        ...prev,
                                        incorrect_word_count: n,
                                    }));
                                    setIsSaved(false);
                                }}
                                sx={{ width: 220 }}
                            />

                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    onClick={() => handleSubmitManualGrade()}
                                    disabled={isSaved}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setManualGradeForm({
                                            id: 0,
                                            sentence_score_id: sentenceScore.id,
                                            user_id: 0,
                                            manual_grade: null,
                                            has_extra_or_missing: null,
                                            incorrect_word_count: null,
                                            sentence_score_manual_individual_grade_list: null,
                                            created_at: null,
                                            updated_at: null
                                        })
                                        setIsSaved(false);
                                    }}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </FormControl>


            </>)
            }
            {
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
                    <Table size="small">
                        <colgroup>
                            {/* (100% - 3 skinny cols * 1%) / 2 = calc(50% - 1.5%) each */}
                            <col style={{ width: 'calc(50% - 1.5%)' }} />  {/* Col 1: flexible */}
                            <col style={{ width: '1%' }} />                {/* Col 2: skinny */}
                            <col style={{ width: 'calc(50% - 1.5%)' }} />  {/* Col 4: flexible */}
                            <col style={{ width: '1%' }} />                {/* Col 5: skinny */}
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell>Text</TableCell>
                                <TableCell></TableCell>
                                <TableCell>Result</TableCell>
                                <TableCell sortDirection={sortBy === 'date' ? sortOrder : false}>
                                    <TableSortLabel
                                        active={sortBy === 'date'}
                                        direction={sortBy === 'date' ? sortOrder : 'asc'}
                                        onClick={() => {
                                            const isAsc = sortBy === 'date' && sortOrder === 'asc';
                                            setSortBy('date');
                                            setSortOrder(isAsc ? 'desc' : 'asc');
                                            setPage(0);
                                        }}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell width={160} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedRows.map(grade => {
                                const isIndividual = Array.isArray(grade.sentence_score_manual_individual_grade_list) && grade.sentence_score_manual_individual_grade_list.length > 0;
                                return (
                                    <TableRow key={grade.id} hover data-testid={`manual-grade-row-${grade.id}`}>
                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            {grade.sentence_score_manual_individual_grade_list && grade.sentence_score_manual_individual_grade_list.length ? (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: "wrap" }}>
                                                    {grade.sentence_score_manual_individual_grade_list.map((g, i) => (
                                                        <Box
                                                            key={g.id ? g.id : i}
                                                            sx={{
                                                                minWidth: 28,
                                                                textAlign: 'center',
                                                                lineHeight: 1.1,
                                                                px: 0.5,
                                                                borderRight: i < (grade.sentence_score_manual_individual_grade_list!.length - 1) ? '1px solid rgba(0,0,0,0.12)' : 'none'
                                                            }}
                                                        >
                                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                                                                {g.pinyin_transcript || '\u00A0'}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {g.transcript_char || '\u00A0'}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.35 }} >
                                                                {g.pinyin_spoken || '\u00A0'}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">{grade.sentence_score ? grade.sentence_score.text : "ERR"}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => playSentenceScoreAudio(grade.sentence_score_id)} disabled={isPlaying}>
                                                {(isPlaying && playingAudioSentenceScoreId == grade.sentence_score_id) ? (
                                                    <CircularProgress size={24} color="inherit" style={{ width: '36px', height: '36px' }} />
                                                ) : (
                                                    <img src={SpeakerBlueIcon} style={{ width: '36px', height: '36px' }} />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            {isIndividual
                                                ? renderIndividualGrades(grade)
                                                : renderSummaryTriple(grade)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(grade.updated_at ? grade.updated_at : grade.created_at)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    aria-label={`Edit manual grade ${grade.id}`}
                                                    onClick={() => { handleEditRow(grade.id); setLastFetchByRandom(false); }}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    startIcon={<DeleteIcon />}
                                                    aria-label={`Delete manual grade ${grade.id}`}
                                                    onClick={() => openDelete(grade)}
                                                >
                                                    Delete
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {listLoading && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Stack direction="row" justifyContent="center" py={2}>
                                            <CircularProgress size={24} />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!listLoading && pagedRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center" color="text.secondary" py={2}>
                                            No records
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Stack direction="row" justifyContent="flex-end">
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setRowsPerPage(n);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </Stack>
                </TableContainer>
            }
            <Dialog open={deleteOpen} onClose={closeDelete}>
                <DialogTitle>Delete Grade</DialogTitle>
                <DialogContent>
                    <Typography>Delete grade for {deleteTarget && deleteTarget.sentence_score ? deleteTarget.sentence_score.text : "ERR"}?<br />
                        This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDelete}>Cancel</Button>
                    <Button color="error" onClick={handleConfirmDelete} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>



        </Stack >
    )
}
